/**
 * MilestoneCardGenerator — portal-rendered modal that lets users export/share
 * an achievement milestone card as an image.
 *
 * Usage:
 *   <MilestoneCardGenerator open={open} onClose={close} badge={badge} userName={user?.name} />
 *
 * Pattern mirrors GenericPostStoryGenerator — html2canvas loaded dynamically,
 * html-native composition, supports story (9:16) and square (1:1) ratios.
 */

import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Share2, RectangleVertical, Send, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import client from '../api/client';
import { useNativeShare } from '../hooks/useNativeShare';
import { templateFor } from './MilestoneCardTemplates';
import { getDisplayName } from '../lib/displayName';

const FONT = "'Barlow Condensed', sans-serif";

const RATIOS = {
  story: { w: 1080, h: 1920, label: '9:16', previewW: 220 },
  square: { w: 1080, h: 1080, label: '1:1', previewW: 260 },
};

export default function MilestoneCardGenerator({ open, onClose, badge, userName = '' }) {
  const displayUserName = getDisplayName(userName);
  const { t } = useTranslation();
  const [ratio, setRatio] = useState('story');
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [posting, setPosting] = useState(false);
  const slideRef = useRef(null);
  const { shareImage, downloadImage } = useNativeShare();

  if (!open || !badge) return null;

  const r = RATIOS[ratio];
  const Template = templateFor(badge);
  const previewH = r.previewW * (r.h / r.w);
  const previewScale = r.previewW / r.w;

  const captureCanvas = async () => {
    if (!slideRef.current) throw new Error('No ref');
    const { default: html2canvas } = await import('html2canvas');
    return html2canvas(slideRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      width: r.w,
      height: r.h,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      downloadImage(canvas, `trainedbyrs-${badge.id}.png`);
      toast.success(t('achievements.cardDownloaded', 'Card saved!'));
    } catch {
      toast.error(t('achievements.cardExportFailed', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const canvas = await captureCanvas();
      const result = await shareImage(canvas, {
        title: 'trainedbyRS',
        text: `${badge.title} — ${badge.description}. Join me on trainedbyRS!`,
        fileName: `trainedbyrs-${badge.id}.png`,
        successMessage: t('achievements.cardDownloaded', 'Card downloaded — share it!'),
        errorMessage: t('achievements.cardShareFailed', 'Failed to share'),
      });
      if (result === 'shared') {
        toast.success(t('social.storyShared', 'Shared successfully!'));
      }
    } catch {
      toast.error(t('achievements.cardShareFailed', 'Failed to share'));
    } finally {
      setSharing(false);
    }
  };

  const handlePostToFeed = async () => {
    setPosting(true);
    try {
      const canvas = await captureCanvas();
      const imageData = canvas.toDataURL('image/png');
      await client.post('/social/posts/achievement', {
        badge_id: badge.id,
        badge_icon: badge.icon || '🏆',
        badge_title: badge.title,
        badge_description: badge.description,
        badge_category: badge.category || null,
        badge_image: imageData,
      });
      toast.success(t('achievements.postedToFeed', 'Posted to your feed!'));
      onClose();
    } catch {
      toast.error(t('achievements.postToFeedFailed', 'Failed to post to feed'));
    } finally {
      setPosting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-start overflow-y-auto p-4 pb-8"
      data-testid="milestone-card-generator"
    >
      <div className="w-full max-w-xs">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-white font-bold text-xl uppercase tracking-wide"
            style={{ fontFamily: FONT }}
          >
            {t('achievements.shareCard', 'Share Milestone')}
          </h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white p-1"
            data-testid="milestone-card-close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Ratio toggle */}
        <div className="flex gap-2 mb-5">
          {Object.entries(RATIOS).map(([key, val]) => (
            <button
              key={key}
              onClick={() => setRatio(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                ratio === key ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:text-white'
              }`}
            >
              {key === 'story' ? (
                <RectangleVertical className="h-3 w-3" />
              ) : (
                <Square className="h-3 w-3" />
              )}
              {val.label}
            </button>
          ))}
        </div>

        {/* Canvas preview — template renders at full export size, CSS-scaled for display */}
        <div className="flex justify-center mb-5">
          <div
            style={{
              width: r.previewW,
              height: previewH,
              overflow: 'hidden',
              flexShrink: 0,
              borderRadius: 12,
              position: 'relative',
            }}
          >
            <div
              style={{
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <div
                ref={slideRef}
                style={{ width: r.w, height: r.h }}
              >
                <Template
                  badge={badge}
                  userName={displayUserName}
                  previewW={r.w}
                  previewH={r.h}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={handlePostToFeed}
            disabled={sharing || exporting || posting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold disabled:opacity-50 transition-colors"
            data-testid="milestone-card-post-feed"
          >
            {posting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {t('achievements.postToFeed', 'Post to Feed')}
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleShare}
              disabled={sharing || exporting || posting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold disabled:opacity-50 transition-colors"
              data-testid="milestone-card-share"
            >
              {sharing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              {t('social.share', 'Share')}
            </button>
            <button
              onClick={handleExport}
              disabled={sharing || exporting || posting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold disabled:opacity-50 transition-colors"
              data-testid="milestone-card-download"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {t('social.download', 'Download')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
